import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import type { RunState } from '@/shared/api/generated/reconciliation';

import { stateLabelKey, toneOf } from '../model/reconciliation';

/* A run's state as the reference's squared tag: a tinted ground, a ringed dot, and the label's words.
 *
 * **One component for all three screens rather than a chip per screen**, which is the failure
 * CLAUDE.md §9 records six screens committing before `StatusBadge` existed: an identical outlined
 * `<span>` drawn locally, so one state rendered two ways depending on which screen a reader was on.
 * Here the stakes are higher than tidiness, because two of the six states are *refusals* — the tag is
 * the only place a reader is told that "keys disjoint" is the platform declining rather than failing,
 * and a refusal that looked red on the definitions list and amber on the runs list would teach both
 * readings at once.
 *
 * The `title` carries the same sentence the outcome-vocabulary panel spells out, so a reader who meets
 * a refusal on the definitions list — where that panel is not — can still find out what it means
 * without navigating.
 *
 * Deliberately not the kit's `StatusBadge`. That component is a pill with a radius; the reference's
 * design system is square throughout, and this tag also carries the ringed-dot treatment the reference
 * uses for a state whose tint alone would not survive a low-contrast display.
 */
export function RunStateTag({ state }: { readonly state: RunState }): ReactElement {
  const { t } = useTranslation();
  return (
    <span
      className={`lq-tag lq-tag--rc-${toneOf(state)}`}
      title={t(`reconciliation.stateHint.${state}`)}
    >
      <span className="lq-tag__dot" aria-hidden="true" />
      {t(stateLabelKey(state))}
    </span>
  );
}
